import React from 'react';
import {
  Box,
  Button,
  Grid,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { formatDistanceToNow, parseJSON, format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import {
  useInternalSupportDashboard,
  useInternalSupportDashboardMy,
} from '../../../data/support/useSupport';
import { useInternalAccounts } from '../../../data/useInternalAdmin';
import {
  SupportCaseStatus,
  SupportCasePriority,
} from '../../../types/support.types';
import AQDataGrid from '../../AQDataGrid';
import CaseStatusChip from './CaseStatusChip';
import SLAIndicator from './SLAIndicator';

const STATUS_OPTIONS: SupportCaseStatus[] = [
  'open',
  'pending',
  'waiting_on_customer',
  'resolved',
  'closed',
];

const PRIORITY_OPTIONS: SupportCasePriority[] = [
  'low',
  'medium',
  'high',
  'critical',
];

function formatResolution(hours: number | null): string {
  if (hours == null) return '--';
  if (hours < 1) return `${Math.round(hours * 60)}m`;
  if (hours < 24) return `${hours.toFixed(1)}h`;
  return `${(hours / 24).toFixed(1)}d`;
}

const InternalSupportDashboard: React.FC = () => {
  const navigate = useNavigate();
  const dashboardQuery = useInternalSupportDashboard();
  const dashboardMyQuery = useInternalSupportDashboardMy();

  const dashboard = dashboardQuery.data;
  const dashboardMy = dashboardMyQuery.data;

  const [statusFilter, setStatusFilter] = React.useState<SupportCaseStatus[]>(
    [],
  );
  const [priorityFilter, setPriorityFilter] = React.useState<
    SupportCasePriority[]
  >([]);
  const [accountFilter, setAccountFilter] = React.useState<string>('');

  const accountsQuery = useInternalAccounts({ size: 200 });
  const accounts = React.useMemo(() => {
    const data = accountsQuery.data?.data || [];
    return data
      .map((a) => ({ id: a.id, name: a.attributes.title || a.attributes.name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [accountsQuery.data]);

  const rows = React.useMemo(() => {
    let cases = dashboardMy?.assigned_cases || [];
    if (statusFilter.length) {
      cases = cases.filter((c) => statusFilter.includes(c.status));
    }
    if (priorityFilter.length) {
      cases = cases.filter((c) => priorityFilter.includes(c.priority));
    }
    if (accountFilter) {
      cases = cases.filter((c) => c.account_id === accountFilter);
    }
    return cases;
  }, [
    dashboardMy?.assigned_cases,
    statusFilter,
    priorityFilter,
    accountFilter,
  ]);

  const colDefs = React.useMemo(
    () => [
      {
        field: 'case_number',
        headerName: 'Case #',
        width: 100,
        sortable: true,
        filterable: false,
        renderCell: (params: any) => (
          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
            {params.value != null ? `#${params.value}` : '—'}
          </Typography>
        ),
      },
      {
        field: 'subject',
        headerName: 'Subject',
        flex: 1,
        minWidth: 200,
        sortable: true,
        filterable: true,
      },
      {
        field: 'account',
        headerName: 'Account',
        width: 160,
        sortable: false,
        filterable: false,
        renderCell: (params: any) => {
          const account = params.value;
          return (
            <Typography variant="body2">
              {account?.title ||
                account?.name ||
                params.row.account_id?.substring(0, 8) ||
                '—'}
            </Typography>
          );
        },
      },
      {
        field: 'priority',
        headerName: 'Priority',
        width: 120,
        sortable: true,
        filterable: false,
        renderCell: (params: any) => (
          <CaseStatusChip value={params.value} type="priority" />
        ),
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 150,
        sortable: true,
        filterable: false,
        renderCell: (params: any) => (
          <CaseStatusChip value={params.value} type="status" />
        ),
      },
      {
        field: 'created_at',
        headerName: 'SLA',
        width: 140,
        sortable: false,
        filterable: false,
        renderCell: (params: any) => (
          <SLAIndicator
            createdAt={params.row.created_at}
            status={params.row.status}
            priority={params.row.priority}
          />
        ),
      },
      {
        field: 'updated_at',
        headerName: 'Created',
        width: 160,
        sortable: true,
        filterable: false,
        valueGetter: (_value: any, row: any) => row.created_at,
        renderCell: (params: any) => (
          <Tooltip
            title={format(parseJSON(params.row.created_at), 'PPpp')}
            placement="top"
          >
            <Typography variant="body2" color="text.secondary">
              {formatDistanceToNow(parseJSON(params.row.created_at), {
                addSuffix: true,
              })}
            </Typography>
          </Tooltip>
        ),
      },
    ],
    [],
  );

  const gridProps = React.useMemo(
    () => ({
      rowHeight: 52,
      sortingMode: 'client' as const,
      filterMode: 'client' as const,
      paginationMode: 'client' as const,
      disableColumnMenu: true,
      disableRowSelectionOnClick: true,
      onRowClick: (params: any) => {
        navigate(`/internal/support/cases/${params.id}`);
      },
      sx: {
        cursor: 'pointer',
        '& .MuiDataGrid-cell': {
          display: 'flex',
          alignItems: 'center',
        },
      },
      initialState: {
        sorting: {
          sortModel: [
            { field: 'priority', sort: 'desc' as const },
            { field: 'updated_at', sort: 'asc' as const },
          ],
        },
      },
    }),
    [navigate],
  );

  const stats = [
    { label: 'Open Cases', value: dashboard?.open_cases ?? '--' },
    { label: 'My Open Cases', value: dashboardMy?.my_open_cases ?? '--' },
    {
      label: 'Avg Response',
      value: formatResolution(
        dashboard?.avg_first_response_minutes != null
          ? dashboard.avg_first_response_minutes / 60
          : null,
      ),
    },
    {
      label: 'SLA Compliance',
      value:
        dashboard?.sla_compliance_percent != null
          ? `${dashboard.sla_compliance_percent}%`
          : '--',
    },
    { label: 'Unassigned', value: dashboard?.unassigned_count ?? '--' },
    { label: 'Critical / High', value: dashboard?.critical_high_count ?? '--' },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 3,
          maxWidth: 1200,
          margin: '0 auto 24px',
        }}
      >
        <Typography variant="h5">Support Dashboard</Typography>
        <Button
          variant="outlined"
          size="small"
          onClick={() => navigate('/internal/support/cases')}
        >
          View All Cases
        </Button>
      </Box>

      <Grid
        container
        spacing={2}
        sx={{ maxWidth: 1200, margin: '0 auto 24px' }}
      >
        {stats.map((stat) => (
          <Grid item xs={6} sm={4} md={2} key={stat.label}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                border: 1,
                borderColor: 'divider',
                textAlign: 'center',
              }}
            >
              <Typography variant="h4">{stat.value}</Typography>
              <Typography variant="body2" color="text.secondary">
                {stat.label}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Box sx={{ maxWidth: 1200, margin: '0 auto 16px' }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          My Cases
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <TextField
            label="Status"
            size="small"
            select
            SelectProps={{
              multiple: true,
              renderValue: (selected: any) =>
                (selected as SupportCaseStatus[]).map((s) => (
                  <CaseStatusChip key={s} value={s} type="status" />
                )),
            }}
            value={statusFilter}
            onChange={(e) =>
              setStatusFilter(e.target.value as unknown as SupportCaseStatus[])
            }
            sx={{ minWidth: 180 }}
          >
            {STATUS_OPTIONS.map((s) => (
              <MenuItem key={s} value={s}>
                <CaseStatusChip value={s} type="status" />
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="Priority"
            size="small"
            select
            SelectProps={{
              multiple: true,
              renderValue: (selected: any) =>
                (selected as SupportCasePriority[]).map((p) => (
                  <CaseStatusChip key={p} value={p} type="priority" />
                )),
            }}
            value={priorityFilter}
            onChange={(e) =>
              setPriorityFilter(
                e.target.value as unknown as SupportCasePriority[],
              )
            }
            sx={{ minWidth: 160 }}
          >
            {PRIORITY_OPTIONS.map((p) => (
              <MenuItem key={p} value={p}>
                <CaseStatusChip value={p} type="priority" />
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="Account"
            size="small"
            select
            value={accountFilter}
            onChange={(e) => setAccountFilter(e.target.value)}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="">All Accounts</MenuItem>
            {accounts.map((a) => (
              <MenuItem key={a.id} value={a.id}>
                {a.name}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      </Box>

      <AQDataGrid
        id="internal-support-my-cases"
        rows={rows}
        colDefs={colDefs}
        loading={dashboardMyQuery.isLoading}
        gridProps={gridProps}
        customErrorElement={
          dashboardMyQuery.isError ? (
            <Typography color="error" sx={{ p: 2, textAlign: 'center' }}>
              Failed to load cases
            </Typography>
          ) : undefined
        }
      />
    </Box>
  );
};

export default InternalSupportDashboard;
